import { CouponComponent } from 'app/automations/automations-models';
import { Inject, Injectable } from '@angular/core';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Logger, LoggerFactory } from '../../core/logger.service';
import { BehaviorSubject, Observable, ReplaySubject, Subject, defer, forkJoin, of, throwError } from 'rxjs';
import { HttpRequestDecorator } from '../../core/triggerbee/http.request.decorator';
import { TriggerbeeApi } from '../../core/triggerbee/triggerbee-api.enum';
import {
  CustomProfileProperty,
  EmbeddedPlaceholder,
  TagItemList,
  UpdateWidgetLayoutAndPositionRequest,
  Widget,
  WidgetComponent,
  WidgetComponentPatchRequest,
  WidgetContainer,
  WidgetDailyStatisticsResponse,
  WidgetDeviceView,
  WidgetLayout,
  WidgetPosition,
  WidgetSimpleInfo,
  WidgetState,
  WidgetTransition,
  WidgetStatisticsResponse,
  WidgetComponentSubType,
  StatesInUse,
  AccountFileResponse,
  AndOrOperator,
  WidgetPreviewResponse,
} from '../../campaign/widgets.model';
import { catchError, concatMap, finalize, map, take, tap } from 'rxjs/operators';
import { CampaignTemplate, TemplateTag } from '../../campaign/templates/template.model';
import { mergeUnsavedEdits } from 'app/campaign/widget-editor/merge-unsaved-edits';
import { Versioned, VersionedKind, WidgetSaveVersions } from './widget-save-versions';
import WidgetComponentHelpers from '../../campaign/widget-component-helpers';
import CampaignHelpers from '../../campaign/campaign-helpers';
import { SessionCondition } from '../../audience/components/session-condition-group.model';
import { AttributionType, CampaignStatus, OnsiteCampaign } from 'app/campaign/campaign.model';
import { CurrentSiteIdStore } from 'app/core/current-site-id.store';
import { TargetSite } from 'app/core/target-site';
import { SiteContextService } from 'app/core/site-context.service';
import { Environment, ENVIRONMENT_DI } from 'app/environment/environment';
import { FlyoutDataType, FlyoutOpeningData } from 'app/flyout/flyout-models';
import { FlyoutService } from 'app/flyout/flyout.service';
import {
  DateWindow,
  DpWidgetAttributionDailyItem,
  DpWidgetFunnelItem,
  DpWidgetFunnelResponse,
  DpWidgetMetricsDailyItem,
  DpWidgetMetricsResponse,
  buildEventFilter,
  distinctWidgetIds,
  mapAttributionDailyToRevenueGraph,
  mapMetricsDailyToDatesGraph,
  mapMetricsToWidgetsStatistics,
  mergeDailyResponses,
  splitDailyMetricsPerGroup,
  splitIntoDailyWindows,
} from './data-platform-adapters/widget-analytics.adapter';
import { buildSignUpGraphQuery, mapSignUpGraphResponse } from './data-platform-adapters/signup-analytics.adapter';
import { DataPlatformQueryResponse } from 'app/data-platform/data-platform.model';

export interface WidgetPreviewContext {
  // TODO: Move
  campaignId: number;
  campaignSiteId?: number;
  /**
   * The campaign's own account, when it is not the active one: scopes the preview to that site
   * without switching the app's account. Deliberately separate from campaignSiteId, which also
   * decides whether the preview opens on the customer's site or in the flyout.
   */
  previewSiteId?: number;
  purposeId?: number;
  filteredTemplates?: CampaignTemplate[];
  deviceView?: WidgetDeviceView;
}

export interface WidgetChangedElsewhere {
  widgetId: number;
  /** False when the refused save only crossed this tab's own earlier change. */
  bySomeoneElse: boolean;
}

// A save of a newer copy that was refused again is given up after this many merges.
const MAX_MERGES_PER_SAVE = 2;

@Injectable({ providedIn: 'root' })
export class WidgetService {
  public isNewImageBS = new BehaviorSubject<any[]>([undefined, undefined]);
  public statesInUseBS = new BehaviorSubject<StatesInUse>(undefined);
  private readonly logger: Logger;
  private oldContainerIdBS = new BehaviorSubject<number>(null);
  public hasUnpublishedChangesBS = new BehaviorSubject<boolean>(null);
  private readonly versions = new WidgetSaveVersions();
  private readonly lastSaves = new Map<string, Observable<void>>();
  private readonly staticWidgetIds = new Set<number>();
  private readonly changedElsewhereSubject = new Subject<WidgetChangedElsewhere>();
  /** A save was refused because the item changed after it was loaded, and was merged and saved again. */
  public readonly changedElsewhere$ = this.changedElsewhereSubject.asObservable();

  constructor(
    @Inject(ENVIRONMENT_DI) private readonly environment: Environment,
    loggerFactory: LoggerFactory,
    private http: HttpRequestDecorator,
    private readonly siteContextService: SiteContextService,
    private readonly flyoutService: FlyoutService,
    private readonly currentSiteIdStore: CurrentSiteIdStore,
  ) {
    this.logger = loggerFactory.get('WidgetService');
  }

  // Honours a TargetSite override the same way HttpRequestDecorator's header does: the
  // DataPlatform analytics paths name the site in the request *body*, so without this a
  // cross-account caller would send the right header and the wrong filter.
  private currentSiteIds(): number[] {
    const id = TargetSite.get() ?? this.currentSiteIdStore.get();
    return id !== undefined && id !== null ? [id] : [];
  }

  private dpWidgetAnalyticsUrl(path: string): string {
    return `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/data-platform/widget-analytics/${path}`;
  }

  /** `feature` tags the requesting UI surface for DataPlatform query attribution; the `client-` prefix is added here. */
  private dpFeatureParams(feature: string): { feature: string } {
    return { feature: `client-${feature}` };
  }

  /**
   * Posts a daily-endpoint request once per ≤366-day window (the DataPlatform
   * daily cap) and concatenates the responses, so callers can keep passing
   * unbounded legacy ranges like "All time". fillEmptyDays is always sent as
   * false — zero-padding happens client-side in the response mappers, which pad
   * over the full original range regardless of how the requests were windowed.
   */
  private postDailyWindowed<TItem>(
    path: string,
    feature: string,
    startDate: string | Date,
    endDate: string | Date,
    filterForWindow: (window: DateWindow) => object,
  ): Observable<{ items: TItem[] }> {
    const requests = splitIntoDailyWindows(startDate, endDate).map((window) =>
      this.http.post(
        this.dpWidgetAnalyticsUrl(path),
        { filter: filterForWindow(window), fillEmptyDays: false },
        this.dpFeatureParams(feature),
      ),
    );
    return forkJoin(requests).pipe(map((responses: Array<{ items: TItem[] }>) => mergeDailyResponses(responses)));
  }

  public updateHasUnpublishedChanges(widgetId, value = true): Observable<any> {
    const body = [
      {
        operationType: 0,
        path: '/hasUnpublishedChanges',
        op: 'replace',
        from: '/hasUnpublishedChanges',
        value,
      },
    ];
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/widgets/${widgetId}`;
    return this.http.patch(url, body).pipe(tap(() => this.hasUnpublishedChangesBS.next(value)));
  }

  public updateComponentSubType(widgetId, componentId, subType): Observable<any> {
    const body = [
      {
        value: subType,
        path: 'subType',
        op: 'replace',
      },
    ];
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/widgets/${widgetId}/components/${componentId}`;
    return this.http.patch(url, body).pipe(
      tap(() => {
        this.versions.patchedHere('component', componentId);
        this.hasUnpublishedChangesBS.next(true);
      }),
    );
  }

  public updateWidgetComponent(widgetId: number, component: WidgetComponent) {
    this.isNewImageBS.next([true, 0]);
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/widgets/${widgetId}/components/${component.id}`;
    component = this.toUpdateComponentRequest(component);
    return this.saveVersioned('component', widgetId, component, (body) =>
      this.http.put(url, this.toUpdateComponentRequest(body)),
    );
  }

  private toUpdateComponentRequest(component: WidgetComponent): WidgetComponent {
    component = WidgetComponentHelpers.emptyAllBackendTypePropertiesOnComponent(component);
    this.updateWithDotNetComponentType(component, 'Update');
    if (component['children']) {
      component.children.forEach((child: WidgetComponent) => {
        this.updateWithDotNetComponentType(child, 'Update');
      });
    }
    return component;
  }

  // Saves of the same item run one at a time, so each is sent at the version the one before it produced.
  private saveVersioned<T extends Versioned, R extends Versioned>(
    kind: VersionedKind,
    widgetId: number,
    item: T,
    put: (body: T) => Observable<R>,
  ): Observable<R> {
    return defer(() => {
      const key = `${kind}:${item.id}`;
      const previous = this.lastSaves.get(key) ?? of(undefined);
      const done = new ReplaySubject<void>(1);
      this.lastSaves.set(key, done);
      return previous.pipe(
        take(1),
        concatMap(() => this.putVersioned(kind, widgetId, item, put, 0)),
        finalize(() => {
          done.next();
          if (this.lastSaves.get(key) === done) {
            this.lastSaves.delete(key);
          }
        }),
      );
    });
  }

  private putVersioned<T extends Versioned, R extends Versioned>(
    kind: VersionedKind,
    widgetId: number,
    item: T,
    put: (body: T) => Observable<R>,
    merges: number,
  ): Observable<R> {
    // A merged save goes out at the version it was just merged onto, whatever this tab saved before.
    const version = merges === 0 ? this.versions.current(kind, item.id, item.version) : item.version;
    return put(version === item.version ? item : { ...item, version }).pipe(
      tap((saved) => this.versions.saved(kind, version, saved)),
      catchError((error: unknown) =>
        error instanceof HttpErrorResponse && error.status === HttpStatusCode.Conflict && merges < MAX_MERGES_PER_SAVE
          ? this.saveOverNewerCopy(kind, widgetId, item, version, put, merges + 1, error)
          : throwError(() => error),
      ),
    );
  }

  // The item changed after this copy was loaded: the user's change is put on top of the newer copy and saved again.
  // Where both changed the same value the newer copy wins, and without the loaded copy nothing is saved over it.
  private saveOverNewerCopy<T extends Versioned, R extends Versioned>(
    kind: VersionedKind,
    widgetId: number,
    edited: T,
    sentVersion: string | undefined,
    put: (body: T) => Observable<R>,
    merges: number,
    conflict: HttpErrorResponse,
  ): Observable<R> {
    // The editor's copy follows this tab's own saves, so the copy they produced is what the user's change is made on.
    const base =
      this.versions.copy<T>(kind, edited.id, sentVersion) ?? this.versions.copy<T>(kind, edited.id, edited.version);
    const widget$ = this.staticWidgetIds.has(widgetId)
      ? this.getStaticWidgetById(widgetId)
      : this.getWidgetById(widgetId);
    return widget$.pipe(
      concatMap((widget) => {
        const reloaded = WidgetSaveVersions.find(widget, kind, edited.id) as T;
        if (!reloaded) {
          return throwError(() => conflict);
        }
        const bySomeoneElse = !this.versions.madeHere(kind, edited.id, reloaded.version);
        const newer = WidgetSaveVersions.ownContent(kind, reloaded);
        const merged = base
          ? mergeUnsavedEdits(base, WidgetSaveVersions.ownContent(kind, edited), newer).merged
          : newer;
        const save$ =
          JSON.stringify(merged) === JSON.stringify(newer)
            ? of(reloaded as unknown as R)
            : this.putVersioned(kind, widgetId, { ...merged, version: reloaded.version }, put, merges);
        return save$.pipe(
          tap(() => this.changedElsewhereSubject.next({ widgetId, bySomeoneElse: bySomeoneElse || !base })),
        );
      }),
    );
  }

  public updateMultipleWidgetComponents(widgetId: number, componentsPathRequest: WidgetComponentPatchRequest) {
    this.isNewImageBS.next([true, 0]);
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/widgets/${widgetId}/components`;
    return this.http.patch(url, componentsPathRequest);
  }

  public loadPlaceholders(): Observable<Array<EmbeddedPlaceholder>> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/embedded-placeholders';
    return this.http.get(url);
  }

  public createPlaceholders(placeholder: EmbeddedPlaceholder): Observable<EmbeddedPlaceholder> {
    placeholder['$type'] = undefined;
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/embedded-placeholders';
    return this.http.post(url, placeholder);
  }

  public updatePlaceholders(placeholder: EmbeddedPlaceholder): Observable<EmbeddedPlaceholder> {
    placeholder['$type'] = undefined;
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/embedded-placeholders/${placeholder.id}`;
    return this.http.put(url, placeholder);
  }

  public deletePlaceholder(id: number): Observable<void> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/embedded-placeholders/${id}`;
    return this.http.delete(url);
  }

  public moveComponentBetweenContainers(
    widgetId: number,
    oldContainerId: number,
    component: WidgetComponent,
    newContainerId: number,
    order: number,
  ): Observable<WidgetComponent> {
    this.isNewImageBS.next([true, 0]);
    const url =
      this.http.getBaseUrl(TriggerbeeApi.Gateway) +
      `/widgets/${widgetId}/containers/${oldContainerId}/components/${component.id}/move`;
    return this.http.put(url, { containerId: newContainerId, order: order });
  }

  public moveContainers(widgetId: number, stateId: number, containerId: number, order: number): Observable<any> {
    this.isNewImageBS.next([true, 0]);
    const url =
      this.http.getBaseUrl(TriggerbeeApi.Gateway) +
      `/widgets/${widgetId}/states/${stateId}/containers/${containerId}/move`;
    return this.http.put(url, { order });
  }

  public moveWidgets(widgetId: number, order: number): Observable<any> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/widgets/${widgetId}/move`;
    return this.http.put(url, order).pipe(tap(() => true));
  }

  /**
   * Creates a component. sourceAutomationId copies that automation onto the new component and links it in the
   * same request, which is how a duplicated component keeps its automation.
   */
  public createWidgetComponent(
    widgetId: number,
    stateId: number,
    containerId: number,
    component: WidgetComponent,
    position?: number,
    sourceAutomationId?: string,
  ) {
    const url =
      this.http.getBaseUrl(TriggerbeeApi.Gateway) +
      `/widgets/${widgetId}/states/${stateId}/containers/${containerId}/components`;
    const queryParams: { position?: number; sourceAutomationId?: string } = {};
    if (position) {
      queryParams.position = position;
    }
    if (sourceAutomationId) {
      queryParams.sourceAutomationId = sourceAutomationId;
    }
    component = WidgetComponentHelpers.emptyAllBackendTypePropertiesOnComponent(component);
    this.updateWithDotNetComponentType(component, 'Create');
    if (component['children']) {
      component.children.forEach((child: WidgetComponent) => {
        this.updateWithDotNetComponentType(child, 'Create');
        if (child['children']) {
          child.children.forEach((grandchild: WidgetComponent) => {
            this.updateWithDotNetComponentType(grandchild, 'Create');
          });
        }
      });
    }
    return this.http
      .post(url, component, queryParams)
      .pipe(tap((created: WidgetComponent) => this.versions.rememberComponent(created)));
  }

  public createWidgetContainer(
    widgetId: number,
    stateId: number,
    container: WidgetContainer,
  ): Observable<WidgetContainer> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/widgets/${widgetId}/states/${stateId}/containers`;
    return this.http.post(url, container);
  }

  public deleteWidget(widgetId: number) {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/widgets/${widgetId}`;
    return this.http.delete(url);
  }

  public deleteWidgetState(widgetId: number, stateId: number) {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/widgets/${widgetId}/states/${stateId}`;
    return this.http.delete(url);
  }

  public deleteWidgetContainer(widgetId: number, stateId: number, containerId: number) {
    const url =
      this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/widgets/${widgetId}/states/${stateId}/containers/${containerId}`;
    return this.http.delete(url);
  }

  public deleteWidgetComponent(widgetId: number, containerId: number, componentId: number) {
    const url =
      this.http.getBaseUrl(TriggerbeeApi.Gateway) +
      `/widgets/${widgetId}/containers/${containerId}/components/${componentId}`;
    return this.http.delete(url);
  }

  /** Coupon components across the site, targetable before any traffic has seen them. */
  public getCouponComponents(): Observable<Array<CouponComponent>> {
    return this.http.get(this.getWidgetGatewayResource('/coupon-components'));
  }

  public getWidgetById(widgetId: number): Observable<Widget> {
    return this.http
      .get(this.getWidgetGatewayResource(`/${widgetId}`))
      .pipe(tap((widget: Widget) => this.versions.rememberWidget(widget)));
  }

  public getStaticWidgetById(widgetId: number): Observable<Widget> {
    return this.http.get(this.getWidgetGatewayResource(`/${widgetId}/static`)).pipe(
      tap((widget: Widget) => {
        this.staticWidgetIds.add(widgetId);
        this.versions.rememberWidget(widget);
      }),
    );
  }

  public getWidgetsByCampaignId(campaignId: number): Observable<Array<Widget>> {
    return this.http.get(this.getWidgetGatewayResource(), { campaignId: campaignId });
  }

  public getWidgetsByAudienceId(audienceId: string): Observable<Array<Widget>> {
    return this.http.get(this.getWidgetGatewayResource(), { audienceId: audienceId });
  }

  public getWidgetsByAudienceIdFromAllAccounts(audienceId: string): Observable<Array<Widget>> {
    return this.http.get(this.getWidgetGatewayResource() + '/by-audience', {
      audienceId: audienceId,
    });
  }

  public getWidgetsBySessionConditionGroupId(sessionConditionGroupId: number): Observable<Array<Widget>> {
    return this.http.get(this.getWidgetGatewayResource(), {
      SessionConditionGroupId: sessionConditionGroupId,
    });
  }

  public getWidgetsWithConsent(hasConsent: boolean): Observable<Array<Widget>> {
    return this.http.get(this.getWidgetGatewayResource(), { hasConsent: hasConsent });
  }

  public getAllWidgets(): Observable<Array<Widget>> {
    return this.http.get(this.getWidgetGatewayResource());
  }

  public getAllWidgetsSimpleInfo(): Observable<Array<WidgetSimpleInfo>> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/simple-widgets';
    return this.http.get(url);
  }

  public updateWidget(widgetId: number, widget: Widget) {
    widget = CampaignHelpers.emptyAllBackendTypeProperties(widget) as Widget;
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/widgets/${widgetId}`;
    return this.http.put(url, widget);
  }

  public updateWidgetName(widgetId: number, name: string) {
    return this.patchWidget(widgetId, '/name', name);
  }

  public updateWidgetAudience(widgetId: number, audienceId: string) {
    return this.patchWidget(widgetId, '/audienceId', audienceId);
  }

  public updateWidgetConditionsAndAudienceOperation(widgetId: number, conditionsAndAudienceOperation: AndOrOperator) {
    return this.patchWidget(widgetId, '/conditionsAndAudienceOperation', conditionsAndAudienceOperation);
  }

  public updatePoweredByDisabled(widgetId: number, isPoweredByDisabled: boolean) {
    return this.patchWidget(widgetId, '/isPoweredByDisabled', isPoweredByDisabled);
  }

  public updateExcludeFromSeoPreview(widgetId: number, excludeFromSeoPreview: boolean) {
    return this.patchWidget(widgetId, '/excludeFromSeoPreview', excludeFromSeoPreview);
  }

  public updateWidgetZIndex(widgetId: number, zIndex: number | null) {
    return this.patchWidget(widgetId, '/zIndex', zIndex);
  }

  public updateWidgetTransition(widgetId: number, transition: WidgetTransition) {
    return this.patchWidget(widgetId, '/transition', transition);
  }

  public patchWidget(widgetId: number, path: string, value: any): Observable<any> {
    const body = [
      {
        value: value,
        path: path,
        op: 'replace',
      },
    ];
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/widgets/${widgetId}`;
    return this.http.patch(url, body);
  }

  public patchPropertiesWidget(widgetId, arr): Observable<any> {
    const body = arr.map((item) => ({ ...item, op: 'replace' }));
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/widgets/${widgetId}`;
    return this.http.patch(url, body);
  }

  public updateWidgetLayout(widgetId: number, widgetLayout: WidgetLayout, widgetPosition: WidgetPosition) {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/widgets/${widgetId}/layout`;
    const body = {
      layout: widgetLayout,
      position: widgetPosition,
    } as UpdateWidgetLayoutAndPositionRequest;
    return this.http.put(url, body);
  }

  public updateNonTeaserStatesPosition(widgetId: number, widgetPosition: WidgetPosition | string) {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/widgets/${widgetId}/states`;
    const body = [
      {
        value: widgetPosition,
        path: '/position',
        op: 'replace',
      },
    ];
    return this.http.patch(url, body);
  }

  public updateSingleStatePosition(
    widgetId: number,
    widgetStateId: number,
    widgetLayout: WidgetLayout,
    widgetPosition: WidgetPosition,
  ) {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/widgets/${widgetId}/states/${widgetStateId}/layout`;
    const body = {
      layout: widgetLayout,
      position: widgetPosition,
    } as UpdateWidgetLayoutAndPositionRequest;
    return this.http.put(url, body);
  }

  public getAllWidgetsStatisticsFromDataPlatform(
    startDate: string,
    endDate: string,
  ): Observable<WidgetStatisticsResponse> {
    return this.getWidgetsStatisticsFromDataPlatform([], startDate, endDate);
  }

  public getWidgetsStatisticsFromDataPlatform(
    widgetIds: Array<number>,
    startDate: string,
    endDate: string,
  ): Observable<WidgetStatisticsResponse> {
    const body = { filter: buildEventFilter(this.currentSiteIds(), startDate, endDate, widgetIds) };
    return this.http
      .post(this.dpWidgetAnalyticsUrl('metrics'), body, this.dpFeatureParams('widgets-statistics'))
      .pipe(map((response: DpWidgetMetricsResponse) => mapMetricsToWidgetsStatistics(response)));
  }

  public getDailyWidgetsStatisticsFromDataPlatform(
    startDate: string,
    endDate: string,
    widgetIds: Array<number> | number[],
  ): Observable<WidgetDailyStatisticsResponse> {
    return this.postDailyWindowed<DpWidgetMetricsDailyItem>(
      'metrics/daily',
      'widgets-daily-statistics',
      startDate,
      endDate,
      (window) => buildEventFilter(this.currentSiteIds(), window.from, window.to, widgetIds as number[]),
    ).pipe(map((response) => mapMetricsDailyToDatesGraph(response, startDate, endDate, true)));
  }

  public getDailyRevenueWidgetsStatistics(
    startDate: string,
    endDate: string,
    widgetIds: Array<number> | number[],
    fillEmptyDates = true,
  ): Observable<WidgetDailyStatisticsResponse> {
    return this.postDailyWindowed<DpWidgetAttributionDailyItem>(
      'attribution/daily',
      'widgets-revenue-graph',
      startDate,
      endDate,
      (window) => buildEventFilter(this.currentSiteIds(), window.from, window.to, widgetIds as number[]),
    ).pipe(map((response) => mapAttributionDailyToRevenueGraph(response, startDate, endDate, fillEmptyDates)));
  }

  // Returns one aggregated chart per group (a group is a campaign's widget ids), aligned to
  // widgetIdGroups by index.
  public getDailyWidgetsStatisticsPerGroup(
    startDate: string,
    endDate: string,
    widgetIdGroups: Array<number[]>,
  ): Observable<WidgetDailyStatisticsResponse[]> {
    if (widgetIdGroups.length === 0) {
      return of([]);
    }
    const allWidgetIds = distinctWidgetIds(widgetIdGroups);
    if (allWidgetIds.length === 0) {
      return of(widgetIdGroups.map(() => mapMetricsDailyToDatesGraph({ items: [] }, startDate, endDate, true)));
    }
    return this.postDailyWindowed<DpWidgetMetricsDailyItem>(
      'metrics/daily',
      'widgets-daily-statistics',
      startDate,
      endDate,
      (window) => buildEventFilter(this.currentSiteIds(), window.from, window.to, allWidgetIds),
    ).pipe(map((response) => splitDailyMetricsPerGroup(response, widgetIdGroups, startDate, endDate)));
  }

  public getDailySignUpAnalyticsStatistics(
    startDate: string,
    endDate: string,
    widgetIds: Array<number> | number[],
    feature: string,
  ): Observable<WidgetDailyStatisticsResponse> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/data-platform/query`;
    const body = buildSignUpGraphQuery(this.currentSiteIds(), startDate, endDate, widgetIds as number[]);
    return this.http
      .post(url, body, this.dpFeatureParams(feature))
      .pipe(map((response: DataPlatformQueryResponse) => mapSignUpGraphResponse(response, startDate, endDate)));
  }

  public getDailyWidgetsStatisticsForWidgets(
    startDate: string | Date,
    endDate: string | Date,
    widgetIds: Array<number>,
    forceIdInRequest = false,
  ): Observable<WidgetDailyStatisticsResponse> {
    const filterWidgetIds = forceIdInRequest ? widgetIds : undefined;
    return this.postDailyWindowed<DpWidgetMetricsDailyItem>(
      'metrics/daily',
      'widgets-daily-statistics',
      startDate,
      endDate,
      (window) => buildEventFilter(this.currentSiteIds(), window.from, window.to, filterWidgetIds),
    ).pipe(map((response) => mapMetricsDailyToDatesGraph(response, startDate, endDate, true)));
  }

  public getWidgetsStatisticsBySiteIdsFromDataPlatform(
    siteIds: number[],
    startDate: string,
    endDate: string,
  ): Observable<WidgetStatisticsResponse> {
    const body = { filter: buildEventFilter(siteIds, startDate, endDate) };
    return this.http
      .post(this.dpWidgetAnalyticsUrl('metrics'), body, this.dpFeatureParams('widgets-statistics-by-site'))
      .pipe(map((response: DpWidgetMetricsResponse) => mapMetricsToWidgetsStatistics(response)));
  }

  public getDailyWidgetsStatisticsBySiteIdsFromDataPlatform(
    siteIds: number[],
    startDate: string,
    endDate: string,
  ): Observable<WidgetDailyStatisticsResponse> {
    return this.postDailyWindowed<DpWidgetMetricsDailyItem>(
      'metrics/daily',
      'widgets-daily-statistics-by-site',
      startDate,
      endDate,
      (window) => buildEventFilter(siteIds, window.from, window.to),
    ).pipe(map((response) => mapMetricsDailyToDatesGraph(response, startDate, endDate, true)));
  }

  public getWidgetsFunnelStatistics(widgetIds: number[]): Observable<DpWidgetFunnelItem[]> {
    return this.http
      .post(this.dpWidgetAnalyticsUrl('funnel'), { siteId: this.currentSiteIds()[0], widgetIds })
      .pipe(map((response: DpWidgetFunnelResponse) => response.items));
  }

  private checkDifferenceMargin(container, device, original) {
    if (original) {
      if (
        container.mobile !== null &&
        container.margin &&
        container.margin.roundValue !== '' &&
        device !== WidgetDeviceView.Mobile
      ) {
        return (
          !original.margin ||
          original.margin.roundValue !== container.margin.roundValue ||
          original.margin.bottom !== container.margin.bottom ||
          original.margin.top !== container.margin.top ||
          original.margin.right !== container.margin.right ||
          original.margin.left !== container.margin.left
        );
      }
    }
  }

  public updateWidgetContainer(
    widgetId: number,
    stateId: number,
    container: WidgetContainer,
    device?,
    originalComponent?,
  ) {
    this.isNewImageBS.next([true, 0]);
    if (this.checkDifferenceMargin(container, device, originalComponent)) {
      container.mobile.margin = { ...container.margin };
    }
    container = CampaignHelpers.emptyAllBackendTypeProperties(container) as WidgetContainer;
    const url =
      this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/widgets/${widgetId}/states/${stateId}/containers/${container.id}`;
    return this.http.put(url, container);
  }

  public createWidgetState(widgetId: number, state: WidgetState) {
    state = CampaignHelpers.emptyAllBackendTypeProperties(state) as WidgetState;
    delete state.order;
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/widgets/${widgetId}/states`;
    return this.http.post(url, state).pipe(tap((created: WidgetState) => this.versions.rememberState(created)));
  }

  public duplicateWidgetState(widgetId: number, widgetStateId: number): Observable<WidgetState> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/widgets/${widgetId}/states/${widgetStateId}/duplicate`;
    return this.http.post(url, null).pipe(tap((created: WidgetState) => this.versions.rememberState(created)));
  }

  public updateWidgetState(widgetId: number, state: WidgetState) {
    state = CampaignHelpers.emptyAllBackendTypeProperties(state) as WidgetState;
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/widgets/${widgetId}/states/${state.id}`;
    return this.saveVersioned('state', widgetId, state, (body) =>
      this.http.put(url, CampaignHelpers.emptyAllBackendTypeProperties(body)),
    );
  }

  public moveWidgetState(widgetId: number, widgetStateId: number, order: number): Observable<any> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/widgets/${widgetId}/states/${widgetStateId}/move`;
    return this.http.put(url, { order: order });
  }

  public getWidgetState(widgetId: number, state: WidgetState): Observable<WidgetState> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/widgets/${widgetId}/states/${state.id}`;
    return this.http.get(url);
  }

  public prepareWidgetPreview(widgetId: number): Observable<WidgetPreviewResponse> {
    const url = this.getWidgetGatewayResource(`/${widgetId}/preview`);
    return this.http.post(url, {});
  }

  public getAllWidgetTemplates(): Observable<Array<CampaignTemplate>> {
    return this.http.get(this.getWidgetTemplateResource(undefined));
  }

  public getAllWidgetTemplateTags(): Observable<Array<TemplateTag>> {
    return this.http.get(this.getWidgetTemplateTagResource());
  }

  public duplicateWidget(widgetId: number): Observable<Widget> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/widgets/${widgetId}/duplicate`;
    return this.http.post(url, null);
  }

  private getWidgetGatewayResource(subResource?: string): string {
    return this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/widgets` + (subResource || '');
  }

  private getWidgetTemplateResource(subResource?: string): string {
    return this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/onsite-campaign-templates` + (subResource || '');
  }

  private getWidgetTemplateTagResource(subResource?: string): string {
    return this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/onsite-campaign-templates/tags` + (subResource || '');
  }

  private updateWithDotNetComponentType(component: WidgetComponent, operation: string): void {
    let className = `Triggerbee.Widgets.Client.Models.WidgetComponents.${component.type}.${operation}WidgetComponent${component.type}Request`;

    if (component.subType === WidgetComponentSubType.ShareCouponCode) {
      className = `Triggerbee.Widgets.Client.Models.WidgetComponents.${component.subType}.${operation}WidgetComponent${component.subType}Request`;
    }

    if (component.subType === WidgetComponentSubType.CodeEditorHtml) {
      className = `Triggerbee.Widgets.Client.Models.WidgetComponents.${component.type}.${operation}WidgetComponent${component.subType}Request`;
    }

    const assemblyName = `Triggerbee.Widgets.Client`;
    component.$type = `${className}, ${assemblyName}`;
  }

  public uploadImage(formData: FormData, category?: string, collection?: string): Observable<AccountFileResponse> {
    const route = !category
      ? '/files/images'
      : !collection
        ? `/files/images?category=${category}`
        : `/files/images?category=${category}&collection=${collection}`;
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + route;
    return this.http.upload(url, formData);
  }

  public uploadVideo(formData: FormData, collection?: string): Observable<AccountFileResponse> {
    const route = !collection ? '/files/videos' : `/files/videos?collection=${collection}`;
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + route;
    return this.http.upload(url, formData);
  }

  public uploadImageAdmin(formData: FormData, category: string, collection: string): Observable<AccountFileResponse> {
    const route = `/admin/files/images?category=${category}&collection=${collection}`;
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + route;
    return this.http.upload(url, formData);
  }

  public uploadVideoAdmin(formData: FormData, collection?: string): Observable<AccountFileResponse> {
    const route = !collection ? '/admin/files/videos' : `/admin/files/videos?collection=${collection}`;
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + route;
    return this.http.upload(url, formData);
  }

  public uploadFontFile(formData: FormData): Observable<any> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/files/fonts`;
    return this.http.upload(url, formData);
  }

  public deleteImage(name: string): Observable<void> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/files/media/${name}`;
    return this.http.delete(url);
  }

  public deleteMedia(names: Array<string>): Observable<void> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/files/media/batch`;
    return this.http.delete(url, null, names);
  }

  public deleteMediaFilesAdmin(names: Array<string>): Observable<void> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/admin/files/media/batch`;
    return this.http.delete(url, null, names);
  }

  public moveMediaFiles(urls: Array<string>, destination: string): Observable<void> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/files/media/move';
    return this.http.post(url, { mediaUrls: urls, destination: destination });
  }

  public moveMediaFilesAdmin(urls: Array<string>, destination: string): Observable<void> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/admin/files/media/move';
    return this.http.post(url, { mediaUrls: urls, destination: destination });
  }

  public getLoadedMediaFiles(): Observable<Array<AccountFileResponse>> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/files/media`;
    return this.http.get(url);
  }

  public getLoadedMediaFilesAdmin(): Observable<Array<AccountFileResponse>> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/admin/files/media`;
    return this.http.get(url);
  }

  public copyMediaCollectionImage(uri: string): Observable<AccountFileResponse> {
    const gatewayUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/files/images/copy-triggerbee-image`;
    return this.http.post(gatewayUrl, JSON.stringify(uri));
  }

  set oldContainerId(id) {
    this.oldContainerIdBS.next(id);
  }

  get oldContainerId(): number {
    return this.oldContainerIdBS.getValue();
  }

  public static flyoutPreviewWidth = 720;

  public previewWidget(
    accountDomainName: string,
    widgetId: number,
    overrideDomainName: string,
    path = '',
    context: WidgetPreviewContext, // TODO: Move all settigns to this interface
    protocol,
    accountHasVisits: boolean,
    productToursPreviewStateId: number = undefined,
    token: string = undefined,
  ): Window | null {
    const previewSiteUrl = this.environment.previewSiteUrl;
    const siteId = context.previewSiteId ?? this.siteContextService.getCurrentSiteId();
    const isTemplatePreview = !context.campaignSiteId;
    const previewUrl = WidgetService.getPreviewWidgetUrl(
      previewSiteUrl,
      siteId,
      accountDomainName,
      widgetId,
      overrideDomainName,
      path,
      isTemplatePreview,
      protocol,
      accountHasVisits,
      productToursPreviewStateId,
      token,
    );
    if (isTemplatePreview || !accountHasVisits) {
      this.flyoutService.open({
        type: FlyoutDataType.WidgetPreview,
        width: WidgetService.flyoutPreviewWidth,
        widgetPreviewContext: {
          widgetId: widgetId,
          campaignId: context.campaignId,
          previewSiteId: context.previewSiteId,
          filteredTemplates: context.filteredTemplates,
          purposeId: context.purposeId,
          deviceView: context.deviceView,
        },
      } as FlyoutOpeningData);
      return null;
    } else {
      return window.open(previewUrl, '_blank');
    }
  }

  public static getPreviewWidgetUrl(
    previewSiteUrl: string,
    siteId: number,
    accountDomainName: string,
    widgetId: number,
    overrideDomainName: string,
    path = '',
    isTemplatePreview: boolean,
    protocol,
    accountHasVisits: boolean,
    productToursPreviewStateId: number = undefined,
    token: string = undefined,
  ): string {
    protocol = protocol ? protocol[0] : 'https://';
    const shouldUseOurPreviewSite = isTemplatePreview || !accountHasVisits;
    const params = WidgetService.getPreviewParameters(
      widgetId,
      siteId,
      isTemplatePreview,
      shouldUseOurPreviewSite,
      productToursPreviewStateId,
      token,
    );
    if (path === '/') {
      path = '';
    }

    if (shouldUseOurPreviewSite) {
      return `${previewSiteUrl}${params}`;
    }

    if (overrideDomainName && overrideDomainName !== '') {
      if (overrideDomainName.toLowerCase().indexOf('http') !== 0) {
        overrideDomainName = `${protocol}${overrideDomainName}`;
      }
      return `${overrideDomainName}${path}${params}`;
    }
    return `${protocol}${accountDomainName}${path}${params}`;
  }

  public static getPreviewParameters(
    widgetId: number,
    siteId: number,
    isTemplatePreview: boolean,
    shouldUseOurPreviewSite: boolean,
    productToursPreviewStateId: number = undefined,
    token: string = undefined,
  ): string {
    const previewTypeQueryName = isTemplatePreview ? 'tb-template-preview' : 'tb-widget-preview';
    let previewParameters = `?${previewTypeQueryName}=${widgetId}&tb-nocache=true`;
    if (shouldUseOurPreviewSite) {
      previewParameters += `&site-id=${siteId}`;
    }
    if (productToursPreviewStateId) {
      previewParameters += `&sticky-css-selector=${productToursPreviewStateId}&token=${token}`;
    }
    return previewParameters;
  }

  public createOrUpdateWidgetConditions(
    widget: Widget,
    sessionConditionGroupId: number,
    sessionConditions: Array<SessionCondition>,
  ) {
    const conditionSettings = {} as {
      sessionConditionGroupId: number;
      sessionConditions: Array<SessionCondition>;
    };
    conditionSettings.sessionConditionGroupId = sessionConditionGroupId;
    conditionSettings.sessionConditions = sessionConditions;
    conditionSettings?.sessionConditions?.forEach((condition) => {
      condition['$type'] = undefined;
    });
    return this.http.post(this.getWidgetGatewayResource() + '/conditions/' + widget.id, conditionSettings);
  }

  public downloadWidgetStatistics(campaign: OnsiteCampaign): Observable<any> {
    const widgetIds = campaign.widgets.map((widget) => widget.id);
    const startDate = campaign.createdTime;
    const endDate = campaign.status === CampaignStatus.Unpublished ? campaign.lastUnpublished : new Date();
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/analytics/export-performance`;

    return this.http.postDownload(url, {
      widgetIds: widgetIds,
      startDate: startDate,
      endDate: endDate,
    });
  }

  public downloadWidgetStatisticsForAccount(widgetIds: number[], startDate: string, endDate: string): Observable<any> {
    const request = {
      widgetIds: widgetIds,
      startDate: startDate,
      endDate: endDate,
    };
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/analytics/export-performance/account`;
    return this.http.postDownload(url, request);
  }

  public downloadWidgetStatisticsForSiteIds(
    siteIds: number[],
    startDate: string,
    endDate: string,
    widgetIds?: number[],
  ): Observable<unknown> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/analytics/export-performance/by-sites`;
    const request: { siteIds: number[]; startDate: string; endDate: string; widgetIds?: number[] } = {
      siteIds,
      startDate,
      endDate,
    };
    // Omitted rather than sent empty, so an unnarrowed export stays the exact request it has always been.
    if (widgetIds?.length) {
      request.widgetIds = widgetIds;
    }
    return this.http.postDownload(url, request);
  }

  public downloadWidgetStatisticsForCustomerAdmin(customerId: string) {
    const url =
      this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/admin/analytics/export-performance/customer/${customerId}`;
    return this.http.getDownload(url, null);
  }

  downloadFile(data: any, type: string, name?: any) {
    const blob = new Blob([data], { type: type });
    const url = window.URL.createObjectURL(blob);
    const fileName = name?.endsWith('.csv') ? name : `${name || 'export'}-${new Date().toISOString().slice(0, 10)}.csv`;
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  public getAllTags(): Observable<{ data: Array<string> }> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/tags';
    return this.http.get(url);
  }

  public getCustomProfileProperties(): Observable<Array<CustomProfileProperty>> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/custom-profile-properties';
    return this.http.get(url);
  }

  public getListTags(): Observable<Array<TagItemList>> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/tags/list';
    return this.http.get(url);
  }

  public createListTag(request: TagItemList): Observable<TagItemList> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/tags';
    return this.http.post(url, request);
  }

  public deleteListTag(id: number): Observable<boolean> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/tags/${id}`;
    return this.http.delete(url);
  }

  public updateListTag(tag: TagItemList): Observable<TagItemList> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/tags/${tag.id}`;
    return this.http.put(url, tag);
  }

  public updateAttribution(widgetId: number, attributionType: AttributionType): Observable<any> {
    return this.patchWidget(widgetId, '/attributionType', attributionType);
  }

  get statesInUse() {
    return this.statesInUseBS.value;
  }
}
